import { useMemo } from 'react';
import { motion } from 'motion/react';

/**
 * Chuva de Six Coins caindo da parte de cima da tela — 3D REAL.
 *
 * Arquitetura 3D:
 *  - Outer container com `perspective: 600px` cria espaço 3D
 *  - Inner motion.div com `transform-style: preserve-3d` rotaciona em Y
 *  - DUAS FACES (front + back) com `backface-visibility: hidden`
 *  - Face traseira tem `transform: rotateY(180deg)` → invisível quando frente
 *    aponta pra câmera, visível quando passa de 90°
 *
 * Otimizações de performance (v2):
 *  - SVG gradients definidos UMA vez num `<defs>` raiz, referenciados via `<use>`
 *    em cada face → reduz parsing de gradients de N×4 pra 4 total
 *  - `transform: translateZ(0)` força GPU layer em containers
 *  - Counts reduzidos por padrão pra dar mais headroom em mobile
 *  - Linear ease no rotateY (mais leve que cubic-bezier interpolation)
 */
interface CoinShowerProps {
  count?: number;
  durationMs?: number;
  minSize?: number;
  maxSize?: number;
}

export function CoinShower({
  count = 22,
  durationMs = 2200,
  minSize = 32,
  maxSize = 52,
}: CoinShowerProps) {
  const coins = useMemo(
    () =>
      Array.from({ length: count }).map((_, i) => ({
        id: i,
        startXPct: Math.random() * 100,
        drift: (Math.random() - 0.5) * 22,
        size: minSize + Math.random() * (maxSize - minSize),
        spinTurns: 1.5 + Math.random() * 3.5,
        spinDir: Math.random() > 0.4 ? 1 : -1,
        zRotation: (Math.random() - 0.5) * 80,
        delaySec: Math.random() * 1.4,
        durationSec: (durationMs / 1000) * (0.85 + Math.random() * 0.35),
      })),
    [count, durationMs, minSize, maxSize],
  );

  return (
    <div className="fixed inset-0 pointer-events-none z-[55] overflow-hidden">
      {/* Gradients compartilhados — definidos UMA vez, usados por todas as moedas. */}
      <svg
        width="0"
        height="0"
        style={{ position: 'absolute', pointerEvents: 'none' }}
        aria-hidden="true"
      >
        <defs>
          <radialGradient id="sixCoinFace" cx="32%" cy="30%" r="80%">
            <stop offset="0%" stopColor="#fef3c7" />
            <stop offset="30%" stopColor="#fcd34d" />
            <stop offset="65%" stopColor="#f59e0b" />
            <stop offset="100%" stopColor="#b45309" />
          </radialGradient>
          <linearGradient id="sixCoinRim" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#fde68a" />
            <stop offset="100%" stopColor="#92400e" />
          </linearGradient>
          <symbol id="sixCoinSymbol" viewBox="0 0 40 40">
            <circle cx="20" cy="20" r="19" fill="url(#sixCoinRim)" />
            <circle cx="20" cy="20" r="16" fill="url(#sixCoinFace)" />
            <ellipse cx="14" cy="13" rx="5" ry="3" fill="white" opacity="0.5" />
            <text
              x="20"
              y="28"
              textAnchor="middle"
              fontSize="22"
              fontFamily="-apple-system, BlinkMacSystemFont, sans-serif"
              fontWeight="900"
              fill="#7c2d12"
            >
              6
            </text>
          </symbol>
        </defs>
      </svg>

      {coins.map((c) => (
        <motion.div
          key={c.id}
          initial={{
            top: '-12%',
            left: `${c.startXPct}%`,
            opacity: 0,
          }}
          animate={{
            top: '112%',
            left: `${c.startXPct + c.drift}%`,
            opacity: [0, 1, 1, 0],
          }}
          transition={{
            duration: c.durationSec,
            delay: c.delaySec,
            ease: [0.35, 0.05, 0.55, 1],
            opacity: {
              duration: c.durationSec,
              delay: c.delaySec,
              times: [0, 0.08, 0.92, 1],
            },
          }}
          style={{
            position: 'absolute',
            width: c.size,
            height: c.size,
            perspective: '500px',
            willChange: 'top, left, opacity',
            transform: 'translateZ(0)', // força GPU layer
          }}
        >
          <motion.div
            initial={{ rotateY: 0, rotateZ: 0 }}
            animate={{
              rotateY: c.spinTurns * 360 * c.spinDir,
              rotateZ: c.zRotation,
            }}
            transition={{
              duration: c.durationSec,
              delay: c.delaySec,
              ease: 'linear',
            }}
            style={{
              width: '100%',
              height: '100%',
              position: 'relative',
              transformStyle: 'preserve-3d',
              willChange: 'transform',
            }}
          >
            <CoinFace />
            <CoinFace back />
          </motion.div>
        </motion.div>
      ))}
    </div>
  );
}

/**
 * Uma face da moeda. Usa `<use>` pra referenciar o symbol compartilhado —
 * gradients são renderizados/parsed UMA vez no <defs> raiz.
 */
function CoinFace({ back = false }: { back?: boolean }) {
  return (
    <svg
      viewBox="0 0 40 40"
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        backfaceVisibility: 'hidden',
        WebkitBackfaceVisibility: 'hidden',
        transform: back ? 'rotateY(180deg)' : undefined,
        filter:
          'drop-shadow(0 4px 8px rgba(251, 191, 36, 0.4)) drop-shadow(0 2px 3px rgba(0, 0, 0, 0.35))',
      }}
      aria-hidden="true"
    >
      <use href="#sixCoinSymbol" />
    </svg>
  );
}
